import { useState, useCallback, useEffect } from 'react';
import { useAuth } from 'context/auth/Auth';
import followApi from 'api/requests/follow';

// Module-level store so all instances following the same user stay in sync
const _store = new Map();        // userId → boolean
const _listeners = new Map();    // userId → Set<setState>
const _toggledIds = new Set();   // ids manually toggled this session (user choice wins over API)

function _notify(userId, value) {
    _store.set(userId, value);
    _listeners.get(userId)?.forEach(fn => fn(value));
}

function _subscribe(userId, setState) {
    if (!_listeners.has(userId)) _listeners.set(userId, new Set());
    _listeners.get(userId).add(setState);
    return () => _listeners.get(userId)?.delete(setState);
}

/**
 * @param {number|string} userId - id of the user being followed (producer/establishment owner)
 * @param {boolean} initialFollowing
 *
 * The event page and the establishment page each gate unauthenticated clicks with a
 * different login modal (one local, one from a shared context), so this hook doesn't
 * own that decision — toggleFollow takes an onRequireAuth callback instead.
 */
export default function useFollow(userId, initialFollowing = false) {
    const { isAuth } = useAuth();

    if (userId && !_toggledIds.has(userId)) {
        _store.set(userId, initialFollowing);
    }

    const [isFollowing, setIsFollowing] = useState(() => _store.get(userId) ?? false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setIsFollowing(_store.get(userId) ?? false);
        return _subscribe(userId, setIsFollowing);
    }, [userId]);

    const toggleFollow = useCallback(async (onRequireAuth) => {
        if (!isAuth) {
            onRequireAuth?.();
            return;
        }

        if (loading || !userId) return;

        const newValue = !_store.get(userId);
        const previousValue = _store.get(userId);

        _toggledIds.add(userId);
        _notify(userId, newValue);
        setLoading(true);

        try {
            if (newValue) {
                await followApi.follow(userId);
            } else {
                await followApi.unfollow(userId);
            }
        } catch (error) {
            _notify(userId, previousValue);
        } finally {
            setLoading(false);
        }
    }, [isAuth, loading, userId]);

    return { isFollowing, toggleFollow, loading };
}
